import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    FormGroup,
    FormControlLabel,
    Checkbox,
    FormLabel,
    RadioGroup,
    Radio,
    Tabs,
    Tab,
    Snackbar,
    Chip,
    Avatar,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    MenuList,
    ClickAwayListener,
    Popper
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    School as EducationIcon,
    Work as WorkIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Description as FileIcon,
    AccessTime as TimeIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import { useParams, useLocation } from 'react-router-dom';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import LaboratoryService from '../../../services/laboratoryService';
import UnfreezeApiService from '../../../services/UnfreezeApiService';
import FileUploadComponent from '../../../components/FileUploadComponent';
import {
    insertDANC,
    updateDANCBySourceId,
    getDANC,
} from '../../../services/apiCalibration';
import {
    getScrutinyRemarks,
    createScrutinyRemark,
    getClauseNo
} from '../../../services/api';
import s3Utils from '../../../utils/s3Utils';

const LabStaffSection = () => {
    const { orgId: urlOrgId } = useParams();
    const location = useLocation();
    const [asstypeId, setAssessTypeId] = useState(null)
    // Function to get orgId from URL query parameters with decryption
    const getOrgIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                return encryptedOrgId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('📍 LabStaffSection - Decrypted appId from URL query:', decryptedAppId);
                console.log('📍 LabStaffSection - Original encrypted appId:', encryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('⚠️ LabStaffSection - Failed to decrypt appId, using original:', encryptedAppId, error);
                return encryptedAppId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get assessmentId from URL query parameters with decryption
    const getAssessmentIdFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessmentId = searchParams.get("assessmentId");
        if (encryptedAssessmentId) {
            try {
                const decryptedAssessmentId = decryptText(encryptedAssessmentId);
                console.log(
                    "📍 Using decrypted assessmentId from URL query:",
                    decryptedAssessmentId
                );
                return decryptedAssessmentId;
            } catch (error) {
                return encryptedAssessmentId;
            }
        }
        return 0;
    };
    const getAssessmentTypeIdFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessmentTypeId = searchParams.get("assessmentTypeId");
        if (encryptedAssessmentTypeId) {
            try {
                const decryptedAssessmentTypeId = decryptText(encryptedAssessmentTypeId);
                console.log(
                    "📍 Using decrypted assessmentTypeId from URL query:",
                    decryptedAssessmentTypeId
                );
                setAssessTypeId(decryptedAssessmentTypeId)
                return decryptedAssessmentTypeId;
            } catch (error) {
                return error;
            }
        }
        return 0;
    };

    // Function to get labType from URL query parameters with decryption
    const getLabTypeFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedLabType = searchParams.get("labType");
        if (encryptedLabType) {
            try {
                const decryptedLabType = decryptText(encryptedLabType);
                console.log(
                    "📍 Using decrypted labType from URL query:",
                    decryptedLabType
                );
                return decryptedLabType;
            } catch (error) {
                return encryptedLabType;
            }
        }
        return null;
    };

    // Function to get assessorId from URL query parameters with decryption
    const getAssessorIdFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessorId = searchParams.get("assessorId");
        if (encryptedAssessorId) {
            try {
                const decryptedAssessorId = decryptText(encryptedAssessorId);
                console.log(
                    "📍 Using decrypted assessorId from URL query:",
                    decryptedAssessorId
                );
                return decryptedAssessorId;
            } catch (error) {
                return encryptedAssessorId;
            }
        }
        return 0;
    };

    const getCapacityFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedCapacity = searchParams.get("capacity");
        if (encryptedCapacity) {
            try {
                const decryptedCapacity = decryptText(encryptedCapacity);
                console.log(
                    "📍 Using decrypted capacity from URL query:",
                    decryptedCapacity
                );
                return decryptedCapacity;
            } catch (error) {
                return encryptedCapacity;
            }
        }
        return null;
    };

    const getAssessmentId = () => {
        const queryAssessmentId = getAssessmentIdFromQuery();
        if (queryAssessmentId) {
            return queryAssessmentId;
        }
        return 0;
    };

    const getLabType = () => {
        const queryLabType = getLabTypeFromQuery();
        if (queryLabType) {
            return queryLabType;
        }
        return null;
    };

    const getAssessorId = () => {
        const queryAssessorId = getAssessorIdFromQuery();
        if (queryAssessorId) {
            return queryAssessorId;
        }
        return 0;
    };

    const getCapacity = () => {
        const queryCapacity = getCapacityFromQuery();
        if (queryCapacity) {
            return queryCapacity;
        }
        return null;
    };

    const userTypeId = localStorage.getItem('userTypeId');

    // Get assessmentId and assessorId for userType 6 (Assessment)
    const assessmentId = getAssessmentId();
    const assessorId = getAssessorId();

    // Function to get orgId with priority: URL query (encrypted) > URL params > null
    const getOrgId = useCallback(() => {
        // First check URL query parameters (encrypted) - highest priority
        const queryOrgId = getOrgIdFromQuery();
        if (queryOrgId) {
            console.log('📍 LabStaffSection - Using decrypted orgId from URL query:', queryOrgId);
            return queryOrgId;
        }

        // Then check URL parameters (unencrypted)
        if (urlOrgId) {
            console.log('📍 LabStaffSection - Using orgId from URL params:', urlOrgId);
            return urlOrgId;
        }

        // No fallback - orgId must be provided via URL
        console.log('⚠️ LabStaffSection - No orgId found in URL parameters');
        return null;
    }, [getOrgIdFromQuery, urlOrgId]);

    // Function to get appId with priority: URL query (encrypted) only
    const getAppId = useCallback(() => {
        // Only check URL query parameters (encrypted)
        const queryAppId = getAppIdFromQuery();
        if (queryAppId) {
            console.log('📍 LabStaffSection - Using decrypted appId from URL query:', queryAppId);
            return queryAppId;
        }

        // No fallback - appId must be provided via URL query parameters
        console.log('⚠️ LabStaffSection - No appId found in URL query parameters');
        return null;
    }, [getAppIdFromQuery]);

    const [orgId, setOrgId] = useState(() => {
        // Initialize with query parameters first, then URL params
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('🚀 LabStaffSection - Initial orgId from encrypted query:', decryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('Failed to decrypt initial orgId, using original:', encryptedOrgId);
                return encryptedOrgId;
            }
        }
        // Only fallback to URL params, no localStorage
        return urlOrgId || null;
    });

    const [appId, setAppId] = useState(() => {
        // Initialize with query parameters only
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('🚀 LabStaffSection - Initial appId from encrypted query:', decryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('Failed to decrypt initial appId, using original:', encryptedAppId);
                return encryptedAppId;
            }
        }
        // No fallback - must be provided via query parameters
        return null;
    });

    // Update orgId state only (no localStorage)
    const updateOrgId = (newOrgId) => {
        console.log('Updating orgId state:', newOrgId);
        setOrgId(newOrgId);
    };

    // Update appId state only (no localStorage)
    const updateAppId = (newAppId) => {
        console.log('Updating appId state:', newAppId);
        setAppId(newAppId);
    };

    // useEffect to handle parameter updates from URL
    useEffect(() => {
        const currentOrgId = getOrgId();
        if (currentOrgId !== orgId) {
            updateOrgId(currentOrgId);
        }

        const currentAppId = getAppId();
        if (currentAppId !== appId) {
            console.log('LabStaffSection - AppId from URL query params:', currentAppId);
            updateAppId(currentAppId);
        }
    }, [urlOrgId, location.search, orgId, appId, getOrgId, getAppId]);

    const [tabValue, setTabValue] = useState(1);
    const [staffList, setStaffList] = useState([]);
    const [editingStaff, setEditingStaff] = useState(null);
    const [employeeType, setEmployeeType] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedDisciplines, setSelectedDisciplines] = useState([]);
    const [disciplines, setDisciplines] = useState([]);
    const [disciplinesLoading, setDisciplinesLoading] = useState(false);
    const [error, setError] = useState('');

    // Scrutiny Remarks states
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [scrutinyRemarksLoading, setScrutinyRemarksLoading] = useState(false);
    const [isDropdownSelection, setIsDropdownSelection] = useState(false);

    // Clause Number states
    const [clauseNo, setClauseNo] = useState([]);
    const [selectedClause, setSelectedClause] = useState(null);

    // Checkbox and adequacy states
    const [selectedItems, setSelectedItems] = useState([]);
    const [adequacyDialogOpen, setAdequacyDialogOpen] = useState(false);
    const [adequacyStatus, setAdequacyStatus] = useState('');
    const [adequacyRemarks, setAdequacyRemarks] = useState('');

    // State for checkbox dropdown in adequacy popup
    const [selectedRemarksOptions, setSelectedRemarksOptions] = useState([]);
    const [isCheckboxDropdownOpen, setIsCheckboxDropdownOpen] = useState(false);

    const [applicationStage, setApplicationStage] = useState(null); // New state for application stage
    const [assessmenttypeId, setAssessmentTypeId] = useState(null); // New state for application stage

    // Color filter state
    const [colorFilter, setColorFilter] = useState(null);

    // DANC Integration States
    const [dancRecords, setDancRecords] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [adequacyChecks, setAdequacyChecks] = useState({});

    // Unfreeze records state
    const [unfreezeRecords, setUnfreezeRecords] = useState([]);

    // Current user for DANC system - get from auth context or session
    const [currentUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('currentUser')) || 'Admin';
        } catch {
            return 'Admin';
        }
    });

    // Current user ID for DANC system - get numeric ID for Insertedby field
    const [currentUserId] = useState(() => {
        try {
            return parseInt(localStorage.getItem('currentUserId')) || 1;
        } catch {
            return 1;
        }
    });
    // Helper function to extract original filename from S3 key
    const extractOriginalFileName = (s3Key) => {
        if (!s3Key) return '';

        // Handle signed URLs - extract the path before query parameters
        let filePath = s3Key;
        if (s3Key.includes('?X-Amz-Algorithm')) {
            filePath = s3Key.split('?')[0]; // Remove query parameters
        }

        // Handle full S3 path: "applications/67474/staff/1758108505639_a42f2bc3b5024697_medical_logo.jpg"
        const parts = filePath.split('/');
        const fileName = parts[parts.length - 1]; // Get the last part (filename)

        // Extract original name from pattern: timestamp_randomstring_originalname.ext
        const match = fileName.match(/^\d+_[a-f0-9]+_(.+)$/);
        if (match) {
            return match[1]; // Return the original filename part
        }

        // If pattern doesn't match, return the filename as is
        return fileName;
    };

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' // 'success', 'error', 'warning', 'info'
    });

    // Form fields state
    const [formData, setFormData] = useState({
        salutation: '',
        name: '',
        designation: '',
        qualification: '',
        specialization: '',
        experience: '',
        training: '',
        landlineNo: '',
        mobileNo: '',
        email: '',
        reportReview: '',
        partFullTime: '',
        timing: '',
        iso15189Training: '',
        competenceAssessmentPerson: '',
        ceoName: '',
        declarationRemark: ''
    });

    // File state - store file paths and names for database
    const [fileData, setFileData] = useState({
        specimenSignature: {
            fileName: '',
            filePath: ''
        },

        bioData: {
            fileName: '',
            filePath: ''
        },

        qualificationCertificates: {
            fileName: '',
            filePath: ''
        },

        experienceCertificates: {
            fileName: '',
            filePath: ''
        },

        trainingCertificates: {
            fileName: '',
            filePath: ''
        },

        appointmentLetter: {
            fileName: '',
            filePath: ''
        },

        recentPhotograph: {
            fileName: '',
            filePath: ''
        },

        competenceAssessment: {
            fileName: '',
            filePath: ''
        }
    });

    // Snackbar helper function
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const fetchDisciplines = useCallback(async () => {
        setDisciplinesLoading(true);
        setError('');
        try {
            console.log('🔄 LabStaffSection - Fetching disciplines with appId:', appId, 'orgId:', orgId);
            const response = await LaboratoryService.getSelectedDisciplines(appId, orgId);
            if (response.success) {
                setDisciplines(response.data);
            } else {
                setError('Failed to fetch disciplines');
            }
        } catch (error) {
            setError(error.message || 'Failed to fetch disciplines');
        } finally {
            setDisciplinesLoading(false);
        }
    }, [appId, orgId]);

    const fetchStaffList = useCallback(async () => {
        if (!appId || !orgId) return;

        setLoading(true);

        try {
            console.log('📄 LabStaffSection - Fetching lab staff with appId:', appId, 'orgId:', orgId);
            const response = await (localStorage.getItem('userTypeId') === '4' ? LaboratoryService.getLabStaffWithDeleted(appId, orgId) : LaboratoryService.getLabStaff(appId, orgId));
            if (response.success) {
                setStaffList(response.data || []);
            } else {
                setError(response.message || 'Failed to load staff list');
            }
        } catch (error) {
            setError('Failed to load staff list');
        } finally {
            setLoading(false);
        }
    }, [appId, orgId]);

    const getApplicationStage = async (appId) => {
        try {
            if (!appId) return;

            console.log('🔄 LabStaffSection - Fetching application stage with appId:', appId);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
            const result = await response.json();

            if (result.success) {
                setApplicationStage(result.stage);
                setAssessmentTypeId(result.assessmentTypeId);
                console.log(result.assessmentTypeId)
            } else {
                console.log('Stage not found, defaulting to stage 1 for demo purposes');
                setApplicationStage(1); // Default to 1 for demo purposes
                setAssessmentTypeId(null);
            }
        } catch (error) {
            console.error('Error fetching application stage:', error);
            console.log('Error occurred, defaulting to stage 1 for demo purposes');
            setApplicationStage(1); // Default to 1 for demo purposes
        }
    };

    // Load DA-NC data function
    const loadDANCData = useCallback(async () => {
        if (!orgId || !appId) return;

        try {
            // Determine expected NCtype based on userTypeId
            const userTypeId = localStorage.getItem('userTypeId');
            let expectedNCType = 'Document Adequacy'; // Default

            if (userTypeId === '4') {
                expectedNCType = 'Document Adequacy';
            } else if (userTypeId === '6') {
                expectedNCType = 'Assessment';
            }

            console.log(`🔍 LabStaffSection - Loading DANC data for userType ${userTypeId} with expected NCtype: ${expectedNCType}`);

            // Get all DA NC records for this org and app
            const response = await getDANC({
                orgid: orgId,
                appid: appId,
                assessorId: assessorId,
                isactive: 1,
            });

            console.log('Staff DA-NC API Response:', response);

            if (
                response.data.success &&
                response.data.data &&
                response.data.data.length > 0
            ) {
                console.log(`📋 Found ${response.data.data.length} total records for LabStaffSection`);

                // Filter Records for Staff section and by NCtype
                const staffDANCRecords = response.data.data.filter(
                    (record) =>
                        (record.SectionName === 'Staff' ||
                            record.SectionName === 'Lab Staff' ||
                            record.SectionName === 'Laboratory Staff') &&
                        record.NCtype === expectedNCType
                );

                console.log(`🎯 Filtered to ${staffDANCRecords.length} staff records with NCtype: ${expectedNCType}`);

                setDancRecords(staffDANCRecords);

                // Create adequacy checks from DA NC data
                const dancAdequacyChecks = {};

                staffDANCRecords.forEach((record) => {
                    let sectionKey = record.sourceid; // Use sourceid as the key for staff

                    if (sectionKey) {
                        dancAdequacyChecks[sectionKey] = {
                            status: record.InformationStatus || '',
                            remarks: record.InformationProvided || record.Observation || '',
                            ClauseNo: record.ClauseNo || '',
                            isSubmitted: false, // Allow users to modify and resubmit
                            submittedAt: record.updatedate || record.Insertdate,
                            dancRecord: record, // Store original record for reference
                        };

                        console.log(
                            `Mapped staff Record to staff ID "${sectionKey}":`,
                            {
                                status: record.InformationStatus,
                                remarks: record.InformationProvided || record.Observation,
                                sourceid: record.sourceid,
                            }
                        );
                    }
                });

                // Merge with existing adequacy checks
                setAdequacyChecks((prev) => {
                    const merged = {
                        ...prev,
                        ...dancAdequacyChecks,
                    };
                    console.log('Final merged staff adequacy checks:', merged);
                    return merged;
                });

                // showSnackbar(
                //   `${staffDANCRecords.length} staff Records loaded and bound to adequacy checks`,
                //   'success'
                // );
            } else {
                console.log('No staff Records found in response');
                setDancRecords([]);
            }
        } catch (error) {
            console.error('Error loading staff DA-NC data:', error);
            //   showSnackbar('Error loading DA-NC data: ' + error.message, 'error');
        }
    }, [orgId, appId]);

    // Load unfreeze records
    const loadUnfreezeRecords = useCallback(async () => {
        if (!appId) return;

        try {
            const response = await UnfreezeApiService.getUnfreezeRecords(appId, orgId);
            if (response.success) {
                console.log('Unfreeze records loaded:', response.data);
                setUnfreezeRecords(response.data);
            }
        } catch (err) {
            console.error("Load unfreeze records error:", err);
        }
    }, [appId, orgId]);

    // Helper function to check if a section is unfrozen based on API data
    const isSectionUnfrozen = (sectionTitle) => {
        if (!unfreezeRecords || unfreezeRecords.length === 0) return false;

        const today = new Date();
        // Reset time to start of day for date-only comparison
        today.setHours(0, 0, 0, 0);

        return unfreezeRecords.some(record => {
            if (record.sectionname !== sectionTitle) return false;

            const expiryDate = new Date(record.expirydate);
            // Reset expiry date time to start of day for date-only comparison
            expiryDate.setHours(0, 0, 0, 0);

            // Return true if expiry date is today or in the future (still valid for editing)
            // Compare dates only, not timestamps
            return expiryDate.getTime() >= today.getTime();
        });
    };

    // Fetch scrutiny remarks from API

    const fetchScrutinyRemarks = useCallback(async () => {
        const type = localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review';
        try {
            setScrutinyRemarksLoading(true);
            console.log('Fetching scrutiny remarks for Lab Staff section...');

            const documentRemarksapi = await getScrutinyRemarks({
                sectionName: 'Staff',
                type: type
            });
            const documentRemarks = documentRemarksapi.data;

            if (documentRemarks.success && documentRemarks.data) {
                setScrutinyRemarks(documentRemarks.data);
                console.log('Loaded scrutiny remarks:', documentRemarks.data.length);
            } else {
                console.warn('No scrutiny remarks found or API returned success: false');
                setScrutinyRemarks([]);
            }
        } catch (error) {
            console.error('Error fetching scrutiny remarks:', error);
            setScrutinyRemarks([]);
        } finally {
            setScrutinyRemarksLoading(false);
        }
    }, []);

    // Fetch clause numbers from API
    const fetchClauseNo = useCallback(async () => {
        try {
            console.log('Fetching clause numbers for Lab Staff section...');

            const clauseResponse = await getClauseNo({ standard: '2022', feild: 'Medical' });

            if (clauseResponse.data?.success && clauseResponse.data?.data) {
                setClauseNo(clauseResponse.data.data);
                console.log('Loaded clause numbers:', clauseResponse.data.data.length);
            } else {
                console.warn('No clause numbers found or API returned success: false');
                setClauseNo([]);
            }
        } catch (error) {
            console.error('Error fetching clause numbers:', error);
            setClauseNo([]);
        }
    }, []);

    // Fetch disciplines from API on component mount
    useEffect(() => {
        if (appId && orgId) {
            getApplicationStage(appId);
            getAssessmentTypeIdFromQuery()
            fetchDisciplines();
            fetchStaffList();
            fetchScrutinyRemarks();
            fetchClauseNo();
        } else {
            setError('Application ID and Organization ID are required. Please ensure you are logged in properly.');
        }
    }, [fetchDisciplines, fetchStaffList, fetchScrutinyRemarks, fetchClauseNo, appId, orgId]);

    // Load DA-NC data when form data is available
    useEffect(() => {
        if (orgId && appId) {
            loadDANCData();
            loadUnfreezeRecords();
        }
    }, [orgId, appId, refreshTrigger, loadDANCData, loadUnfreezeRecords]);

    // Adjust tab index based on applicationStage once it's loaded
    useEffect(() => {
        const requestedTab = sessionStorage.getItem('staff_requested_tab');
        if (requestedTab !== null && applicationStage !== null) {
            const tabIndex = parseInt(requestedTab);

            // Map the original tab indices to the new indices based on stage
            let adjustedTab = tabIndex;

            // When stage is 18/""/null/"18a"/"18b", tabs are: 0=Add/Edit Staff, 1=View Staff (both tabs visible)
            // When stage is other (not null/""/"18"/"18a"/"18b"), tabs are: 0=View Staff (only 1 tab visible)

            if (applicationStage !== null && applicationStage !== 'null' && applicationStage !== '' && applicationStage !== '18' && applicationStage !== '18a' && applicationStage !== '18b' || isSectionUnfrozen('Lab Staff')) {
                // When stage is not null/""/"18"/"18a"/"18b", tabs are: 0=View Staff
                // Original tabs were: 0=Add/Edit Staff, 1=View Staff
                if (tabIndex === 0) {
                    // Add/Edit Staff doesn't exist when stage is not null/""/"18"/"18a"/"18b", go to View Staff
                    adjustedTab = 0;
                }
                // tabIndex 1 (View Staff) stays at 0
                else if (tabIndex === 1) {
                    adjustedTab = 0;
                }
            }
            // For null/""/"18"/"18a", both tabs are visible so no adjustment needed

            setTabValue(adjustedTab);
            sessionStorage.removeItem('staff_requested_tab');

            console.log('🔄 Adjusted staff tab index:', {
                requested: tabIndex,
                adjusted: adjustedTab,
                applicationStage
            });
        }
    }, [applicationStage]);

    // Handle discipline matching when disciplines are loaded and we have editing staff
    useEffect(() => {
        if (editingStaff && disciplines.length > 0 && editingStaff.DisciplineId) {
            // Handle both single discipline ID and comma-separated discipline IDs
            const tableDisciplineIds = editingStaff.DisciplineId.toString().split(',')
                .map(id => id.trim())
                .filter(id => id !== '');

            // Match table discipline IDs with available disciplines
            const matchedDisciplines = [];
            tableDisciplineIds.forEach(tableDisciplineId => {
                const matchingDiscipline = disciplines.find(discipline => {
                    const disciplineIdStr = (discipline.DisciplineId || discipline.Id).toString();
                    const tableIdStr = tableDisciplineId.toString();
                    return disciplineIdStr === tableIdStr;
                });

                if (matchingDiscipline) {
                    const disciplineId = matchingDiscipline.DisciplineId || matchingDiscipline.Id;
                    matchedDisciplines.push(disciplineId);
                }
            });

            setSelectedDisciplines(matchedDisciplines);
        }
    }, [disciplines, editingStaff]);




    const handleFileChange = (fieldName, fileData) => {
        if (fileData) {
            // Extract file path and name for database storage
            const fileName = fileData.originalName || fileData.fileName || fileData.name || '';
            const filePath = fileData.key || fileData.fileName || '';
            const signedUrl = fileData.signedUrl || fileData.url || '';

            setFileData(prev => ({
                ...prev,
                [fieldName]: {
                    fileName: fileName,
                    filePath: filePath,
                    key: filePath,
                    originalName: fileName,
                    signedUrl: signedUrl,
                    storage: 'S3'
                }
            }));
        } else {
            // Clear file data when file is removed
            setFileData(prev => ({
                ...prev,
                [fieldName]: null
            }));
        }
    };

    const saveLabStaff = async () => {
        if (!appId || !orgId) {
            showSnackbar('Application ID and Organization ID are required', 'error');
            return;
        }

        // Validate required fields
        if (!employeeType || employeeType === '') {
            showSnackbar('Please select an Employee Type', 'warning');
            return;
        }

        if (!formData.name || !formData.designation || !formData.qualification ||
            !formData.specialization || !formData.experience || !formData.training ||
            !formData.mobileNo || !formData.email || !formData.reportReview) {
            showSnackbar('Please fill in all required fields', 'warning');
            return;
        }

        // Validate mobile number - must be exactly 10 digits and numeric only
        if (formData.mobileNo.length !== 10 || !/^\d{10}$/.test(formData.mobileNo)) {
            showSnackbar('Mobile number must be exactly 10 digits and contain only numbers', 'warning');
            return;
        }

        // Validate email format
        if (!isValidEmail(formData.email)) {
            showSnackbar('Please enter a valid email address', 'warning');
            return;
        }

        // Additional validation when Report/Review is Yes
        if (formData.reportReview === 'Yes') {
            if (!formData.partFullTime) {
                showSnackbar('Please select Part time / Full time employment type', 'warning');
                return;
            }

            if (formData.partFullTime === 'Part Time' && !formData.timing) {
                showSnackbar('Please specify timing for part-time employment', 'warning');
                return;
            }

            if (!fileData.specimenSignature || !fileData.specimenSignature.fileName) {
                showSnackbar('Please upload specimen signature', 'warning');
                return;
            }

            if (!fileData.staffPhoto || !fileData.staffPhoto.fileName) {
                showSnackbar('Please upload staff photo', 'warning');
                return;
            }
            if (
                !formData.iso15189Training ||
                !formData.competenceAssessmentPerson ||
                !formData.ceoName ||
                !formData.declarationRemark
            ) {
                showSnackbar(
                    'Please fill all declaration and training details',
                    'warning'
                );
                return;
            }

            // Validate all mandatory uploads
            if (!fileData.specimenSignature?.fileName) {
                showSnackbar('Please upload specimen signature', 'warning');
                return;
            }

            if (!fileData.staffPhoto?.fileName) {
                showSnackbar('Please upload recent photograph', 'warning');
                return;
            }

            if (!fileData.bioData?.fileName) {
                showSnackbar('Please upload bio-data file', 'warning');
                return;
            }

            if (!fileData.qualificationCertificates?.fileName) {
                showSnackbar(
                    'Please upload qualification certificates',
                    'warning'
                );
                return;
            }

            if (!fileData.experienceCertificates?.fileName) {
                showSnackbar(
                    'Please upload experience certificates',
                    'warning'
                );
                return;
            }

            if (!fileData.trainingCertificates?.fileName) {
                showSnackbar(
                    'Please upload training certificates',
                    'warning'
                );
                return;
            }

            if (!fileData.appointmentLetter?.fileName) {
                showSnackbar(
                    'Please upload appointment letter',
                    'warning'
                );
                return;
            }

            if (!fileData.competenceAssessment?.fileName) {
                showSnackbar(
                    'Please upload competence assessment file',
                    'warning'
                );
                return;
            }
        }

        try {
            setLoading(true);

            // Prepare lab staff data for API
            const labStaffData = {
                appId: parseInt(appId),
                orgId: parseInt(orgId),
                employeeType: employeeType,
                name: `${formData.salutation} ${formData.name}`.trim(),
                designation: formData.designation,
                qualification: formData.qualification,
                specialization: formData.specialization,
                relevantExperience: formData.experience,
                relevantTraining: formData.training,
                contactNo: formData.mobileNo,
                faxNo: formData.landlineNo || '',
                emailID: formData.email,
                done17025Training: 0, // Default value
                trainingCertFile: '', // Can be extended later
                iso15189Training: formData.iso15189Training,
                competenceAssessmentPerson:
                    formData.competenceAssessmentPerson,

                ceoName: formData.ceoName,

                declarationRemark:
                    formData.declarationRemark,

                done17025Training:
                    formData.iso15189Training === 'Yes' ? 1 : 0,

                isAuthorisedSignatory: formData.reportReview === 'Yes' ? 1 : 0,
                disciplineId: selectedDisciplines.length > 0 ? selectedDisciplines.join(',') : null, // Multiple disciplines as comma-separated
                groupId: null,
                signatorySign: formData.reportReview === 'Yes' && fileData.specimenSignature ?
                    (fileData.specimenSignature.signedUrl || fileData.specimenSignature.filePath) : '',
                remark: '',
                typeofEngagement: formData.reportReview === 'Yes' ? formData.partFullTime : '',
                timming: formData.reportReview === 'Yes' && formData.partFullTime === 'Part Time' ? formData.timing : '',
                panNo: '',
                panDoc: '',
                photo: formData.reportReview === 'Yes' && fileData.staffPhoto ?
                    (fileData.staffPhoto.signedUrl || fileData.staffPhoto.filePath) : '',
                bioDataFile:
                    fileData.bioData?.signedUrl || '',

                qualificationCertificateFile:
                    fileData.qualificationCertificates?.signedUrl || '',

                experienceCertificateFile:
                    fileData.experienceCertificates?.signedUrl || '',

                trainingCertificateFile:
                    fileData.trainingCertificates?.signedUrl || '',

                appointmentLetterFile:
                    fileData.appointmentLetter?.signedUrl || '',

                competenceAssessmentFile:
                    fileData.competenceAssessment?.signedUrl || '',

                // OPTIONAL FILE NAMES
                bioDataFileName:
                    fileData.bioData?.fileName || '',

                qualificationCertificateFileName:
                    fileData.qualificationCertificates?.fileName || '',

                experienceCertificateFileName:
                    fileData.experienceCertificates?.fileName || '',

                trainingCertificateFileName:
                    fileData.trainingCertificates?.fileName || '',

                appointmentLetterFileName:
                    fileData.appointmentLetter?.fileName || '',

                competenceAssessmentFileName:
                    fileData.competenceAssessment?.fileName || '',

                aadhar: '',
                aerbNo: '',
                aerbDocFile: '',
                InsertedById: localStorage.getItem('orgId') || ''
            };

            const response = editingStaff ?
                await LaboratoryService.updateLabStaff(editingStaff.Id, labStaffData) :
                await LaboratoryService.createLabStaff(labStaffData);

            if (response.success) {
                showSnackbar(
                    editingStaff ? 'Lab staff details updated successfully!' : 'Lab staff details saved successfully!',
                    'success'
                );

                clearForm(); // Clear form after successful save/update
                fetchStaffList(); // Refresh staff list
            } else {
                showSnackbar(response.message || 'Failed to save lab staff details', 'error');
            }
        } catch (error) {
            showSnackbar(error.message || 'Failed to save lab staff details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEditStaff = async (staff) => {
        setEditingStaff(staff);
        setTabValue(0); // Switch to Add Staff tab

        // Extract salutation and name from the full name
        const nameParts = staff.Name.split(' ');
        const salutation = nameParts[0].endsWith('.') ? nameParts[0] : '';
        const name = salutation ? nameParts.slice(1).join(' ') : staff.Name;

        // Populate form with staff data based on API response format
        setEmployeeType(staff.EmployeeType || '');
        setFormData({
            salutation: salutation,
            name: name,
            designation: staff.Designation || '',
            qualification: staff.Qualification || '',
            specialization: staff.Specialization || '',
            experience: staff.RelevantExperience || '',
            training: staff.RelevantTraining || '',
            landlineNo: staff.FaxNo || '',
            mobileNo: staff.ContactNo || '',
            email: staff.EmailID || '',
            reportReview: staff.IsAuthorisedSignatory ? 'Yes' : 'No',
            partFullTime: staff.typeofEngagement || '',
            timing: staff.Timming || '',
            iso15189Training: staff.ISO15189Training || '',
            competenceAssessmentPerson:
                staff.CompetenceAssessmentPerson || '',

            ceoName: staff.CEOName || '',

            declarationRemark:
                staff.DeclarationRemark || '',
        });

        // Set selected disciplines if available - match table discipline IDs to available disciplines
        if (staff.DisciplineId) {
            // Handle both single discipline ID and comma-separated discipline IDs
            // Convert to string first, then split and parse
            const tableDisciplineIds = staff.DisciplineId.toString().split(',')
                .map(id => id.trim())
                .filter(id => id !== '');

            // Check if disciplines are available before matching
            if (disciplines && disciplines.length > 0) {
                // Match table discipline IDs with available disciplines and check corresponding checkboxes
                const matchedDisciplines = [];
                tableDisciplineIds.forEach(tableDisciplineId => {
                    // Find matching discipline in available disciplines list
                    // Compare as strings to avoid type mismatch issues
                    const matchingDiscipline = disciplines.find(discipline => {
                        const disciplineIdStr = (discipline.DisciplineId || discipline.Id).toString();
                        const tableIdStr = tableDisciplineId.toString();
                        return disciplineIdStr === tableIdStr;
                    });

                    if (matchingDiscipline) {
                        // Use the correct ID property from the matching discipline - keep as string or number as needed
                        const disciplineId = matchingDiscipline.DisciplineId || matchingDiscipline.Id;
                        matchedDisciplines.push(disciplineId);
                    }
                });

                setSelectedDisciplines(matchedDisciplines);
            } else {
                // If disciplines aren't loaded yet, try direct match - keep as strings
                setSelectedDisciplines(tableDisciplineIds);
            }
        } else {
            setSelectedDisciplines([]);
        }

        // Set file data if available - only set if there's actually a file
        // Use S3 utils for enhanced processing
        const processedSignature = staff.SignatorySign ?
            await s3Utils.processStaffSignatureData(staff) : null;

        const processedPhoto = staff.photo ?
            await s3Utils.processStaffPhotoData(staff) : null;

        const createS3FileObject = (
            fileUrl,
            fileName
        ) => {

            if (!fileUrl) return null;

            // Signed URL
            if (
                fileUrl.includes(
                    '?X-Amz-Algorithm'
                )
            ) {

                return {
                    fileName:
                        fileName ||  s3Utils.extractFileName(fileUrl),

                    filePath: fileUrl,

                    key:
                        fileUrl
                            .split('?')[0]
                            .replace(
                                'https://nabl7t.s3.ap-south-1.amazonaws.com/',
                                ''
                            ),

                    originalName:
                        fileName ||  s3Utils.extractFileName(fileUrl),

                    signedUrl: fileUrl,

                    storage: 'S3'
                };
            }

            // Full URL
            if (fileUrl.startsWith('http')) {

                return {
                    fileName:
                        fileName || s3Utils. s3Utils.extractFileName(fileUrl),

                    filePath: fileUrl,

                    key:
                        fileUrl.replace(
                            'https://nabl7t.s3.ap-south-1.amazonaws.com/',
                            ''
                        ),

                    originalName:
                        fileName ||  s3Utils.extractFileName(fileUrl),

                    signedUrl: fileUrl,

                    storage: 'S3'
                };
            }

            // Filename only
            return {
                fileName:
                    fileName || fileUrl,

                filePath: fileUrl,

                key: `live/${fileUrl}`,

                originalName:
                    fileName || fileUrl,

                signedUrl: null,

                storage: 'S3'
            };
        };

        setFileData({

            specimenSignature:  await createS3FileObject(
                    staff.SignatorySign,
                    extractOriginalFileName(staff.SignatorySign)
                ),

            staffPhoto:  await createS3FileObject(
                    staff.SignatorySign,
                    extractOriginalFileName(staff.SignatorySign)
                ),

            bioData:
                await createS3FileObject(
                    staff.BioDataFile,
                    staff.BioDataFileName
                ),

            qualificationCertificates:
                await createS3FileObject(
                    staff.QualificationCertificateFile,
                    staff.QualificationCertificateFileName
                ),

            experienceCertificates:
                await createS3FileObject(
                    staff.ExperienceCertificateFile,
                    staff.ExperienceCertificateFileName
                ),

            trainingCertificates:
                await createS3FileObject(
                    staff.TrainingCertificateFile,
                    staff.TrainingCertificateFileName
                ),

            appointmentLetter:
                await createS3FileObject(
                    staff.AppointmentLetterFile,
                    staff.AppointmentLetterFileName
                ),

            competenceAssessment:
                await createS3FileObject(
                    staff.CompetenceAssessmentFile,
                    staff.CompetenceAssessmentFileName
                )
        });
    };

    const handleDeleteStaff = async (staffId) => {
        setLoading(true);
        try {
            const response = await LaboratoryService.deleteLabStaff(staffId);
            if (response.success) {
                showSnackbar('Staff member deleted successfully', 'success');
                fetchStaffList(); // Refresh the list
            } else {
                showSnackbar(response.message || 'Failed to delete staff member', 'error');
            }
        } catch (error) {
            showSnackbar(error.message || 'Failed to delete staff member', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDisciplineChange = useCallback((disciplineId, disciplineName, isChecked) => {
        if (isChecked) {
            setSelectedDisciplines(prev => [...prev, disciplineId]);
        } else {
            setSelectedDisciplines(prev => prev.filter(id => id !== disciplineId));
        }
    }, []);

    const handleEmployeeTypeChange = useCallback((event) => {
        const value = event.target.value;
        setEmployeeType(value);
    }, []);

    // Email validation helper function
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Memoized form field change handler
    const handleFormDataChange = useCallback((field, value) => {
        // Mobile number validation - only allow 10 digit numbers
        if (field === 'mobileNo') {
            // Remove any non-digit characters
            const cleanValue = value.replace(/\D/g, '');
            // Limit to 10 digits
            const limitedValue = cleanValue.slice(0, 10);
            setFormData(prev => ({
                ...prev,
                [field]: limitedValue
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const renderFileButton = (fileUrl, fileName) => {
        if (!fileUrl) return '-';

        return (
            <>
                <Chip
                    label={fileName}
                    size="small"
                    color="success"
                    variant="outlined"
                    icon={<FileIcon />}
                    sx={{
                        fontSize: '0.75rem',
                        maxWidth: '140px',
                        '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }
                    }}
                />
                <Tooltip title={fileName || 'View File'}>
                    <IconButton
                        size="small"
                        sx={{
                            color: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#e3f2fd'
                            }
                        }}
                        onClick={() =>
                            s3Utils.handleFileViewWithS3(
                                fileUrl,
                                {
                                    showError: (msg) =>
                                        showSnackbar(msg, 'error'),
                                    expiresIn: 172800
                                }
                            )
                        }
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </>

        );
    };

    // Memoized clear form function
    const clearForm = useCallback(() => {
        setEditingStaff(null);
        setEmployeeType('');
        setFormData({
            salutation: '',
            name: '',
            designation: '',
            qualification: '',
            specialization: '',
            experience: '',
            training: '',
            landlineNo: '',
            mobileNo: '',
            email: '',
            reportReview: '',
            partFullTime: '',
            timing: '',
            iso15189Training: '',
            competenceAssessmentPerson: '',
            declarantName: '',
            declarationRemark: '',
        });
        setSelectedDisciplines([]);
        setFileData({
            specimenSignature: null,
            staffPhoto: null
        });
    }, []);

    // Memoized tab change handler
    const handleTabChange = useCallback((event, newValue) => {
        setTabValue(newValue);
        // When applicationStage is null/""/18/18a, "View Staff" is at index 1
        // When applicationStage is other, "View Staff" is at index 0
        const isViewStaffTab = (applicationStage === null || applicationStage === "" || applicationStage === '18a' || applicationStage === '18' || applicationStage === '18b' || isSectionUnfrozen('Lab Staff')) ? newValue === 1 : newValue === 0;
        if (isViewStaffTab) {
            fetchStaffList(); // Refresh staff list when switching to View Staff tab
        }
    }, [fetchStaffList, applicationStage]);

    // Memoized Add Staff Tab Component
    const renderAddStaffTab = useCallback(() => (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {editingStaff && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Editing staff member: {editingStaff.Name}
                    <Button
                        size="small"
                        onClick={clearForm}
                        sx={{ ml: 2 }}
                    >
                        Cancel Edit
                    </Button>
                </Alert>
            )}

            {/* Employee Type Dropdown */}
            <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel id="employee-type-label">Employee Type</InputLabel>
                        <Select
                            labelId="employee-type-label"
                            value={employeeType}
                            label="Employee Type"
                            onChange={handleEmployeeTypeChange}
                        >
                            <MenuItem value="">Select Employee Type</MenuItem>
                            <MenuItem value="Details of Radiological Safety Officer (For Medical Imaging laboratories)">Details of Radiological Safety Officer (For Medical Imaging laboratories)</MenuItem>
                            <MenuItem value="Head of the laboratory having overall responsibility">Head of the laboratory having overall responsibility</MenuItem>
                            <MenuItem value="Laboratory Director">Laboratory Director</MenuItem>
                            <MenuItem value="Person responsible for management system">Person responsible for management system</MenuItem>
                            <MenuItem value="Person Responsible for Technical Operations">Person Responsible for Technical Operations</MenuItem>
                            <MenuItem value="Others">Others</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 1.5 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            Disciplines
                        </Typography>

                        {disciplinesLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <FormGroup>
                                <Grid container sx={{ maxHeight: '200px', overflowY: 'auto' }} spacing={2}>
                                    {disciplines.map((discipline) => {
                                        // Use consistent ID property - prefer DisciplineId, fallback to Id
                                        const disciplineId = discipline.DisciplineId || discipline.Id;

                                        // Check if this discipline is selected - handle both string and number comparisons
                                        const isChecked = selectedDisciplines.some(selectedId => {
                                            return selectedId.toString() === disciplineId.toString();
                                        });

                                        return (
                                            <Grid item xs={12} key={disciplineId}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={isChecked}
                                                            onChange={(e) => handleDisciplineChange(disciplineId, discipline.DisciplineName, e.target.checked)}
                                                            name={`discipline_${disciplineId}`}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {discipline.DisciplineName}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        p: 1,
                                                        margin: 0,
                                                        width: '100%',
                                                        '&:hover': {
                                                            backgroundColor: '#f5f5f5'
                                                        },
                                                        ...(isChecked && {
                                                            backgroundColor: '#e3f2fd',
                                                            borderColor: '#2196f3'
                                                        })
                                                    }}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {disciplines.length === 0 && !disciplinesLoading && (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                        No disciplines available
                                    </Typography>
                                )}
                            </FormGroup>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Personal Details Form */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Personal Details
                </Typography>

                <Grid container spacing={3}>
                    {/* Row 1 */}
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Salutation</InputLabel>
                            <Select
                                value={formData.salutation}
                                label="Salutation"
                                onChange={(e) => handleFormDataChange('salutation', e.target.value)}
                            >
                                <MenuItem value="Mr.">Mr.</MenuItem>
                                <MenuItem value="Ms.">Ms.</MenuItem>
                                <MenuItem value="Dr.">Dr.</MenuItem>
                                <MenuItem value="Prof.">Prof.</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={9}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) => handleFormDataChange('name', e.target.value)}
                            required
                        />
                    </Grid>

                    {/* Row 2 */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Designation"
                            value={formData.designation}
                            onChange={(e) => handleFormDataChange('designation', e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Qualification"
                            value={formData.qualification}
                            onChange={(e) => handleFormDataChange('qualification', e.target.value)}
                            required
                        />
                    </Grid>

                    {/* Row 3 */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Specialization"
                            value={formData.specialization}
                            onChange={(e) => handleFormDataChange('specialization', e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Relevant Experience (in years)"
                            value={formData.experience}
                            onChange={(e) => handleFormDataChange('experience', e.target.value)}
                            required
                        />
                    </Grid>

                    {/* Row 4 */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Relevant Training"
                            value={formData.training}
                            onChange={(e) => handleFormDataChange('training', e.target.value)}
                            multiline
                            rows={3}
                            required
                        />
                    </Grid>

                    {/* Row 5 */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Landline No."
                            value={formData.landlineNo}
                            onChange={(e) => handleFormDataChange('landlineNo', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Mobile No."
                            value={formData.mobileNo}
                            onChange={(e) => handleFormDataChange('mobileNo', e.target.value)}
                            required
                            error={formData.mobileNo && formData.mobileNo.length !== 10}
                            helperText={
                                formData.mobileNo && formData.mobileNo.length !== 10
                                    ? 'Mobile number must be exactly 10 digits'
                                    : "Enter 10-digit mobile number"
                            }
                            inputProps={{
                                maxLength: 10,
                                pattern: "[0-9]*",
                                inputMode: "numeric"
                            }}
                        />
                    </Grid>

                    {/* Row 6 */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Email ID"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleFormDataChange('email', e.target.value)}
                            required
                            error={formData.email && !isValidEmail(formData.email)}
                            helperText={
                                formData.email && !isValidEmail(formData.email)
                                    ? "Please enter a valid email address"
                                    : "Enter a valid email address (e.g., user@example.com)"
                            }
                        />
                    </Grid>

                    {/* ISO 15189 Training Question */}
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                                Have you undergone 4 days training course of laboratory management system as per ISO 15189? *
                            </FormLabel>

                            <RadioGroup
                                row
                                value={formData.iso15189Training || ''}
                                onChange={(e) =>
                                    handleFormDataChange('iso15189Training', e.target.value)
                                }
                            >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {/* Competence Assessment Person Name */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Name of person who has done the competence assessment"
                            value={formData.competenceAssessmentPerson || ''}
                            onChange={(e) =>
                                handleFormDataChange(
                                    'competenceAssessmentPerson',
                                    e.target.value
                                )
                            }
                        />
                    </Grid>

                    {/* Declaration Section */}
                    {formData.competenceAssessmentPerson && (
                        <Grid item xs={12}>
                            <Paper
                                sx={{
                                    p: 3,
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: '#fafafa'
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{ mb: 2, fontWeight: 600 }}
                                >
                                    Declaration *
                                </Typography>

                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    I,
                                </Typography>

                                <TextField
                                    fullWidth
                                    label="CEO Name"
                                    placeholder="CEO Name"
                                    value={formData.ceoName || ''}
                                    onChange={(e) =>
                                        handleFormDataChange(
                                            'ceoName',
                                            e.target.value
                                        )
                                    }
                                    sx={{ mb: 3 }}
                                />

                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    hereby declare that the above person meets the
                                    qualification and experience requirements for the
                                    scope as per ISO 15189:2022 and NABL criteria
                                </Typography>

                                <TextField
                                    fullWidth
                                    label="If not applicable write NA"
                                    value={formData.declarationRemark || ''}
                                    onChange={(e) =>
                                        handleFormDataChange(
                                            'declarationRemark',
                                            e.target.value
                                        )
                                    }
                                    sx={{ mb: 3 }}
                                />

                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    and has been assessed by me to report, review and
                                    authorization of results in Histopathology discipline
                                    for scope.
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 3,
                                        color: 'text.secondary',
                                        lineHeight: 1.8
                                    }}
                                >
                                    I also understand and accept that in case of the
                                    above information found false or ambiguous, NABL
                                    has the right to take any adverse actions against
                                    the lab as deemed appropriate as per NABL 216.
                                </Typography>

                                <Alert severity="info" sx={{ mt: 3 }}>
                                    <Typography variant="body2">
                                        <strong>Note :</strong> For Specimen Signature
                                        and Recent Photograph of Proposed Person please
                                        upload the file in jpg format. For uploading
                                        other details/records please upload file in pdf
                                        format. Please keep the filenames simple
                                        alphanumeric.
                                    </Typography>
                                </Alert>
                            </Paper>
                        </Grid>
                    )}

                    {/* Report/Review Authorization */}
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                                Report, Review and Authorization *
                            </FormLabel>
                            <RadioGroup
                                row
                                value={formData.reportReview}
                                onChange={(e) => handleFormDataChange('reportReview', e.target.value)}
                            >
                                {asstypeId === '11' ? (
                                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />

                                ) : asstypeId === '12' ? (

                                    <FormControlLabel value="No" control={<Radio />} label="No" />
                                ) : (
                                    <>
                                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="No" control={<Radio />} label="No" />
                                    </>
                                )}
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {/* Conditional fields when Report/Review is Yes */}
                    {formData.reportReview === 'Yes' && (
                        <>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Part time / Full time *</InputLabel>
                                    <Select
                                        value={formData.partFullTime}
                                        label="Part time / Full time *"
                                        onChange={(e) => handleFormDataChange('partFullTime', e.target.value)}
                                    >
                                        <MenuItem value="Full Time">Full Time</MenuItem>
                                        <MenuItem value="Part Time">Part Time</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {formData.partFullTime === 'Part Time' && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Timing *"
                                        value={formData.timing}
                                        onChange={(e) => handleFormDataChange('timing', e.target.value)}
                                        placeholder="e.g., 9 AM to 1 PM"
                                    />
                                </Grid>
                            )}

                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Specimen Signature Upload *
                                    </Typography>
                                    <FileUploadComponent
                                        fieldName="specimenSignature"
                                        label="Upload Specimen Signature"
                                        orgId={orgId}
                                        section="staff"
                                        accept=".jpg,.jpeg,.png"
                                        maxSize={5}
                                        required={false}
                                        onChange={(fieldName, fileData) => handleFileChange(fieldName, fileData)}
                                        initialFile={
                                            fileData.specimenSignature?.fileName || fileData.specimenSignature?.filePath
                                                ? fileData.specimenSignature
                                                : null
                                        }
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Photo Upload *
                                    </Typography>
                                    <FileUploadComponent
                                        fieldName="staffPhoto"
                                        label="Upload Recent Photograph of Proposed Person"
                                        orgId={orgId}
                                        section="staff"
                                        accept=".jpg,.jpeg,.png"
                                        maxSize={5}
                                        required={false}
                                        onChange={(fieldName, fileData) => handleFileChange(fieldName, fileData)}
                                        initialFile={
                                            fileData.staffPhoto?.fileName || fileData.staffPhoto?.filePath
                                                ? fileData.staffPhoto
                                                : null
                                        }
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Bio-data Upload *
                                    </Typography>

                                    <FileUploadComponent
                                        fieldName="bioData"
                                        label="Upload Bio-data of Proposed Person"
                                        orgId={orgId}
                                        section="staff"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        maxSize={5}
                                        required={false}
                                        onChange={(fieldName, fileData) =>
                                            handleFileChange(fieldName, fileData)
                                        }
                                        initialFile={
                                            fileData.bioData?.fileName ||
                                                fileData.bioData?.filePath
                                                ? fileData.bioData
                                                : null
                                        }
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Qualification Certificates Upload *
                                    </Typography>

                                    <FileUploadComponent
                                        fieldName="qualificationCertificates"
                                        label="Upload Qualification Certificates"
                                        orgId={orgId}
                                        section="staff"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        maxSize={5}
                                        required={false}
                                        onChange={(fieldName, fileData) =>
                                            handleFileChange(fieldName, fileData)
                                        }
                                        initialFile={
                                            fileData.qualificationCertificates?.fileName ||
                                                fileData.qualificationCertificates?.filePath
                                                ? fileData.qualificationCertificates
                                                : null
                                        }
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Experience Certificates Upload *
                                    </Typography>

                                    <FileUploadComponent
                                        fieldName="experienceCertificates"
                                        label="Upload Relevant Experience Certificates"
                                        orgId={orgId}
                                        section="staff"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        maxSize={5}
                                        required={false}
                                        onChange={(fieldName, fileData) =>
                                            handleFileChange(fieldName, fileData)
                                        }
                                        initialFile={
                                            fileData.experienceCertificates?.fileName ||
                                                fileData.experienceCertificates?.filePath
                                                ? fileData.experienceCertificates
                                                : null
                                        }
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Training Certificates Upload *
                                    </Typography>

                                    <FileUploadComponent
                                        fieldName="trainingCertificates"
                                        label="Upload Relevant Training Certificates"
                                        orgId={orgId}
                                        section="staff"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        maxSize={5}
                                        required={false}
                                        onChange={(fieldName, fileData) =>
                                            handleFileChange(fieldName, fileData)
                                        }
                                        initialFile={
                                            fileData.trainingCertificates?.fileName ||
                                                fileData.trainingCertificates?.filePath
                                                ? fileData.trainingCertificates
                                                : null
                                        }
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Appointment Letter Upload *
                                    </Typography>

                                    <FileUploadComponent
                                        fieldName="appointmentLetter"
                                        label="Upload Appointment Letter"
                                        orgId={orgId}
                                        section="staff"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        maxSize={5}
                                        required={false}
                                        onChange={(fieldName, fileData) =>
                                            handleFileChange(fieldName, fileData)
                                        }
                                        initialFile={
                                            fileData.appointmentLetter?.fileName ||
                                                fileData.appointmentLetter?.filePath
                                                ? fileData.appointmentLetter
                                                : null
                                        }
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={6}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Competence Assessment Upload *
                                    </Typography>

                                    <FileUploadComponent
                                        fieldName="competenceAssessment"
                                        label="Upload Evidence of Competence Assessment"
                                        orgId={orgId}
                                        section="staff"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        maxSize={5}
                                        required={false}
                                        onChange={(fieldName, fileData) =>
                                            handleFileChange(fieldName, fileData)
                                        }
                                        initialFile={
                                            fileData.competenceAssessment?.fileName ||
                                                fileData.competenceAssessment?.filePath
                                                ? fileData.competenceAssessment
                                                : null
                                        }
                                    />
                                </Paper>
                            </Grid>
                        </>
                    )}

                    {/* Save Button */}
                    {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || isSectionUnfrozen('Lab Staff')) && (
                        <Grid item xs={12} sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={saveLabStaff}
                                disabled={loading}
                                sx={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 600
                                }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                        {editingStaff ? 'Updating...' : 'Saving...'}
                                    </>
                                ) : (
                                    editingStaff ? 'Update Staff' : 'Save Staff'
                                )}
                            </Button>
                            {editingStaff && (
                                <Button
                                    variant="outlined"
                                    onClick={clearForm}
                                    sx={{ ml: 2, px: 4, py: 1.5 }}
                                >
                                    Cancel
                                </Button>
                            )}
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Box>
    ), [
        error,
        editingStaff,
        employeeType,
        disciplinesLoading,
        disciplines,
        selectedDisciplines,
        formData,
        loading,
        fileData,
        handleEmployeeTypeChange,
        handleDisciplineChange,
        handleFormDataChange,
        clearForm,
        orgId
    ]);

    // Checkbox handlers
    const handleSelectItem = useCallback((itemId) => {
        setSelectedItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    }, []);

    const handleSelectAll = useCallback((event) => {
        if (event.target.checked) {
            setSelectedItems(staffList.map(item => item.Id));
        } else {
            setSelectedItems([]);
        }
    }, [staffList]);

    // Helper function to get adequacy status for a staff member
    const getStaffStatus = (staffId) => {
        return adequacyChecks[staffId] || { status: '', remarks: '', ClauseNo: '' };
    };

    // Get row styling based on color field for userTypeId 4
    const getRowStyling = (staff, index) => {
        const userTypeId = localStorage.getItem('userTypeId');

        // Only apply color coding for userTypeId = 4
        if (userTypeId !== '4') {
            return {
                '&:hover': {
                    backgroundColor: '#e8f4fd !important',
                    transition: 'background-color 0.2s ease'
                },
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                borderBottom: '1px solid #e0e0e0',
                '&:last-child td': {
                    borderBottom: 'none'
                }
            };
        }

        // Color mapping based on staff.color field
        const colorMap = {
            'Blue': {
                backgroundColor: '#bbdefb !important',
                color: '#0d47a1',
                borderBottom: '1px solid #64b5f6',
                '&:hover': { backgroundColor: '#90caf9 !important' },
                '& td': {
                    backgroundColor: 'transparent',
                    color: '#0d47a1',
                    fontWeight: 500
                }
            },
            'Black': {
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                color: 'inherit',
                borderBottom: '1px solid #e0e0e0',
                '&:hover': { backgroundColor: '#e8f4fd !important' },
                '& td': { backgroundColor: 'transparent' }
            },
            'Red': {
                backgroundColor: '#ffcdd2 !important',
                color: '#b71c1c',
                borderBottom: '1px solid #e57373',
                '&:hover': { backgroundColor: '#ef9a9a !important' },
                '& td': {
                    backgroundColor: 'transparent',
                    color: '#b71c1c',
                    fontWeight: 500
                }
            },
            'Orange': {
                backgroundColor: '#ffe0b2 !important',
                color: '#e65100',
                borderBottom: '1px solid #ffb74d',
                '&:hover': { backgroundColor: '#ffcc80 !important' },
                '& td': {
                    backgroundColor: 'transparent',
                    color: '#e65100',
                    fontWeight: 500
                }
            }
        };

        const baseStyle = colorMap[staff.color] || colorMap['Black'];
        return {
            ...baseStyle,
            '&:last-child td': {
                borderBottom: 'none'
            }
        };
    };

    // Adequacy popup handlers
    const handleMarkAdequate = useCallback(() => {
        if (selectedItems.length === 0) {
            showSnackbar('Please select at least one staff member to mark as adequate', 'warning');
            return;
        }
        setAdequacyDialogOpen(true);
    }, [selectedItems]);

    const closeAdequacyDialog = useCallback(() => {
        setAdequacyDialogOpen(false);
        setAdequacyStatus('');
        setAdequacyRemarks('');
        setIsDropdownSelection(false);
        setSelectedClause(null);
    }, []);

    // Handler for adequacy remarks with dropdown tracking
    const handleAdequacyRemarksChange = useCallback((newValue, fromDropdown = false) => {
        setAdequacyRemarks(newValue || '');
        setIsDropdownSelection(fromDropdown);
    }, []);

    // Handle checkbox selection for predefined remarks in popup
    const handleCheckboxChange = useCallback((option, checked) => {
        let newSelectedOptions;
        if (checked) {
            newSelectedOptions = [...selectedRemarksOptions, option];
        } else {
            newSelectedOptions = selectedRemarksOptions.filter(item =>
                item.inadequate_remarks !== option.inadequate_remarks
            );
        }
        setSelectedRemarksOptions(newSelectedOptions);

        // Update remarks textbox with comma-separated values
        const remarksText = newSelectedOptions
            .map(item => typeof item === 'string' ? item : item.inadequate_remarks)
            .join(', ');
        handleAdequacyRemarksChange(remarksText, true);
    }, [selectedRemarksOptions, handleAdequacyRemarksChange]);

    // Clear all selected checkboxes in popup
    const handleClearAllCheckboxes = useCallback(() => {
        setSelectedRemarksOptions([]);
        handleAdequacyRemarksChange('', false);
    }, [handleAdequacyRemarksChange]);

    const handleAdequacySubmit = useCallback(async () => {
        if (!adequacyStatus || (adequacyStatus === 'Inadequate' && (!adequacyRemarks?.trim()))) {
            showSnackbar('Please fill all required fields and select from dropdown for inadequate remarks', 'error');
            return;
        }

        setLoading(true);
        try {
            console.log('=== STAFF ADEQUACY SUBMIT ===');
            console.log('Selected staff IDs:', selectedItems);
            console.log('Adequacy Status:', adequacyStatus);
            console.log('Adequacy Remarks:', adequacyRemarks);

            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            // Process each selected staff member
            for (const staffId of selectedItems) {
                try {
                    // Check if Record exists for this staff
                    const existingRecord = dancRecords.find(
                        (record) =>
                            record.sourceid && parseInt(record.sourceid) === parseInt(staffId)
                    );

                    if (existingRecord) {
                        // Update existing Record
                        // Determine NCtype based on userTypeId
                        const userTypeId = localStorage.getItem('userTypeId');
                        let ncType = 'Document Adequacy'; // Default

                        if (userTypeId === '4') {
                            ncType = 'Document Adequacy';
                        } else if (userTypeId === '6') {
                            ncType = 'Assessment';
                        }

                        console.log(`💾 Updating DANC record for staff ${staffId}, userType ${userTypeId} with NCtype: ${ncType}`);

                        const updateData = {
                            subsectionName: 'Staff Details',
                            informationStatus: adequacyStatus,
                            observation:
                                adequacyStatus === 'Inadequate'
                                    ? adequacyRemarks || 'Document adequacy marked as Inadequate'
                                    : 'Document adequacy check completed',
                            NCtype: ncType,
                            sourceid: staffId,
                            isactive: 1,
                            ClauseNo: selectedClause ? (typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')) : null,
                        };

                        console.log(`Updating Record for staff ${staffId}:`, updateData);

                        // Add assessmentId and assessorId for userType 6 (Assessment)
                        let response;
                        if (userTypeId === '6') {
                            response = await updateDANCBySourceId(
                                orgId,
                                appId,
                                'Staff',
                                staffId,
                                assessmentId,
                                assessorId,
                                updateData
                            );
                        } else {
                            response = await updateDANCBySourceId(
                                orgId,
                                appId,
                                'Staff',
                                staffId,
                                0,
                                localStorage.getItem('orgId'),
                                updateData
                            );
                        }

                        if (response.success) {
                            console.log(`✅ Record updated for staff ${staffId}`);
                            successCount++;
                        } else {
                            throw new Error(response.message || 'Failed to Update');
                        }
                    } else {
                        // Create new Record
                        // Determine NCtype based on userTypeId
                        const userTypeId = localStorage.getItem('userTypeId');
                        let ncType = 'Document Adequacy'; // Default

                        if (userTypeId === '4') {
                            ncType = 'Document Adequacy';
                        } else if (userTypeId === '6') {
                            ncType = 'Assessment';
                        }

                        console.log(`✨ Creating new DANC record for staff ${staffId}, userType ${userTypeId} with NCtype: ${ncType}`);

                        const dancData = {
                            Orgid: parseInt(orgId),
                            Appid: parseInt(appId),
                            SectionName: 'Staff',
                            SubsectionName: 'Staff Details',
                            InformationProvided: adequacyRemarks || '',
                            InformationStatus: adequacyStatus,

                            Observation:
                                adequacyStatus === 'Inadequate'
                                    ? adequacyRemarks || 'Document adequacy marked as Inadequate'
                                    : 'Document adequacy check completed',
                            sourceid: staffId,
                            Insertedby: currentUserId,
                            NCtype: ncType,
                            ClauseNo: selectedClause ? (typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')) : null,
                            assessmentId: parseInt(assessmentId) || 0,
                            assessorId: parseInt(assessorId) || localStorage.getItem('orgId')

                        };



                        console.log(`Creating Record for staff ${staffId}:`, dancData);
                        const response = await insertDANC(dancData);

                        if (response.success) {
                            console.log(`✅ Record created for staff ${staffId}`);
                            successCount++;
                        } else {
                            throw new Error(response.message || 'Failed to create Record');
                        }
                    }

                    // Update adequacy checks locally
                    setAdequacyChecks((prev) => ({
                        ...prev,
                        [staffId]: {
                            status: adequacyStatus,
                            remarks: adequacyRemarks,
                            ClauseNo: selectedClause ? (typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')) : '',
                            isSubmitted: true,
                            submittedAt: new Date().toISOString(),
                        },
                    }));
                } catch (error) {
                    console.error(`Error processing staff ${staffId}:`, error);
                    errors.push(`Staff ${staffId}: ${error.message}`);
                    errorCount++;
                }
            }

            // Show results
            if (successCount > 0) {
                showSnackbar(
                    `Adequacy check completed: ${successCount} staff member(s) processed successfully${errorCount > 0 ? `, ${errorCount} failed` : ''
                    }`,
                    errorCount > 0 ? 'warning' : 'success'
                );
            }

            if (errorCount > 0 && errors.length > 0) {
                console.error('Staff adequacy submission errors:', errors);
            }

            // Trigger refresh to reload DA-NC data
            setRefreshTrigger((prev) => prev + 1);

            // Clear selections and close dialog
            setSelectedItems([]);
            closeAdequacyDialog();
        } catch (error) {
            console.error('Error submitting staff adequacy check:', error);
            showSnackbar('Error submitting adequacy check: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [adequacyStatus, adequacyRemarks, selectedItems, dancRecords, orgId, appId, currentUserId, isDropdownSelection, selectedClause, assessmentId, assessorId]);

    // Memoized View Staff Tab Component
    const renderViewStaffTab = useCallback(() => (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Lab Staff List
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {selectedItems.length > 0 && (
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleMarkAdequate}
                                sx={{
                                    px: 3,
                                    py: 1,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                Mark Adequate ({selectedItems.length})
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={fetchStaffList}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} /> : null}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : staffList.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="body1" color="text.secondary">
                            No staff members found. Add staff members from the "Add Staff" tab.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* Color Legend - Only show for userTypeId 4 */}
                        {localStorage.getItem('userTypeId') === '4' && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    mb: 2,
                                    p: 1.5,
                                    backgroundColor: '#f8fafc',
                                    borderRadius: 1,
                                    border: '1px solid #e2e8f0',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                                    Legend:
                                </Typography>
                                <Chip
                                    label="New"
                                    size="small"
                                    onClick={() => setColorFilter(colorFilter === 'Blue' ? null : 'Blue')}
                                    sx={{
                                        backgroundColor: '#e3f2fd',
                                        color: '#1565c0',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        border: colorFilter === 'Blue' ? '2px solid #1565c0' : 'none',
                                        '&:hover': { opacity: 0.8 }
                                    }}
                                />
                                <Chip
                                    label="Existing"
                                    size="small"
                                    onClick={() => setColorFilter(colorFilter === 'Black' ? null : 'Black')}
                                    sx={{
                                        backgroundColor: '#fff',
                                        color: '#000',
                                        border: colorFilter === 'Black' ? '2px solid #000' : '1px solid #e0e0e0',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.8 }
                                    }}
                                />
                                <Chip
                                    label="Deleted"
                                    size="small"
                                    onClick={() => setColorFilter(colorFilter === 'Red' ? null : 'Red')}
                                    sx={{
                                        backgroundColor: '#ffebee',
                                        color: '#c62828',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        border: colorFilter === 'Red' ? '2px solid #c62828' : 'none',
                                        '&:hover': { opacity: 0.8 }
                                    }}
                                />
                                <Chip
                                    label="Updated"
                                    size="small"
                                    onClick={() => setColorFilter(colorFilter === 'Orange' ? null : 'Orange')}
                                    sx={{
                                        backgroundColor: '#fff3e0',
                                        color: '#e65100',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        border: colorFilter === 'Orange' ? '2px solid #e65100' : 'none',
                                        '&:hover': { opacity: 0.8 }
                                    }}
                                />
                                {colorFilter && (
                                    <Chip
                                        label="Clear Filter"
                                        size="small"
                                        onClick={() => setColorFilter(null)}
                                        sx={{
                                            backgroundColor: '#f44336',
                                            color: '#fff',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            '&:hover': { backgroundColor: '#d32f2f' }
                                        }}
                                    />
                                )}
                            </Box>
                        )}

                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                borderRadius: 2,
                                border: '1px solid #e0e0e0'
                            }}
                        >
                            <Table
                                stickyHeader
                                sx={{
                                    borderCollapse: 'separate',
                                    '& .MuiTableCell-root': {
                                        borderRight: '1px solid #e0e0e0',
                                        padding: '12px 16px'
                                    },
                                    '& .MuiTableCell-root:last-child': {
                                        borderRight: 'none'
                                    },
                                    '& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root': {
                                        borderColor: '#c5cae9'
                                    }
                                }}
                            >
                                <TableHead>
                                    <TableRow sx={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    }}>
                                        {((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (localStorage.getItem('userTypeId') === '4' || localStorage.getItem('userTypeId') === '6')) && (
                                            <TableCell sx={{
                                                fontWeight: 600,
                                                color: 'white',
                                                borderBottom: '2px solid #5a67d8',
                                                backgroundColor: 'transparent',
                                                borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                                                width: '50px'
                                            }}>
                                                <Checkbox
                                                    checked={selectedItems.length === staffList.length && staffList.length > 0}
                                                    indeterminate={selectedItems.length > 0 && selectedItems.length < staffList.length}
                                                    onChange={handleSelectAll}
                                                    sx={{
                                                        color: 'white',
                                                        '&.Mui-checked': {
                                                            color: 'white',
                                                        },
                                                        '&.MuiCheckbox-indeterminate': {
                                                            color: 'white',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>S.No</TableCell>
                                        {(applicationStage === "18" || applicationStage === "18a" || applicationStage === "18b" || applicationStage === "20") && (
                                            <TableCell sx={{
                                                fontWeight: 600,
                                                color: 'white',
                                                textAlign: 'center',
                                                borderBottom: '2px solid #5a67d8',
                                                backgroundColor: 'transparent',
                                                borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    Adequacy Status
                                                </Box>
                                            </TableCell>
                                        )}
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <BusinessIcon fontSize="small" />
                                                Employee Type
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PersonIcon fontSize="small" />
                                                Name
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <WorkIcon fontSize="small" />
                                                Designation
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <EducationIcon fontSize="small" />
                                                Qualification
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>Specialization</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TimeIcon fontSize="small" />
                                                Experience
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>Training</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <PhoneIcon fontSize="small" />
                                                Contact
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>Landline</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <EmailIcon fontSize="small" />
                                                Email
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>Discipline</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>Authorization</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>ISO 15189 Training</TableCell>

                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>Competence Assessment Person</TableCell>


                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                                            minWidth: '260px'
                                        }}>
                                            Additional Files
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <FileIcon fontSize="small" />
                                                Specimen Signature / Photo
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>Engagement Type</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>Timing</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            color: 'white',
                                            borderBottom: '2px solid #5a67d8',
                                            backgroundColor: 'transparent',
                                            borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                        }}>Declaration</TableCell>

                                        {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || isSectionUnfrozen('Lab Staff')) && (
                                            <TableCell sx={{
                                                fontWeight: 600,
                                                color: 'white',
                                                borderBottom: '2px solid #5a67d8',
                                                backgroundColor: 'transparent'
                                            }}>Actions</TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {staffList.filter(staff => !colorFilter || staff.color === colorFilter).map((staff, index) => (
                                        <TableRow
                                            key={staff.Id}
                                            sx={getRowStyling(staff, index)}
                                        >
                                            {((applicationStage === "18" || applicationStage === "18b" || applicationStage === "20") && (localStorage.getItem('userTypeId') === '4' || localStorage.getItem('userTypeId') === '6')) && (
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedItems.includes(staff.Id)}
                                                        onChange={() => handleSelectItem(staff.Id)}
                                                        color="primary"
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {index + 1}
                                                </Typography>
                                            </TableCell>
                                            {(applicationStage === "18" || applicationStage === "18a" || applicationStage === "18b" || applicationStage === "20") && (
                                                <TableCell>
                                                    {(() => {
                                                        const status = getStaffStatus(staff.Id);
                                                        return (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                <Box
                                                                    sx={{
                                                                        px: 2,
                                                                        py: 0.5,
                                                                        borderRadius: 1,
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 600,
                                                                        textAlign: 'center',
                                                                        minWidth: '80px',
                                                                        bgcolor: status.status === 'Adequate' ? '#e8f5e8' :
                                                                            status.status === 'Inadequate' ? '#ffebee' : '#f5f5f5',
                                                                        color: status.status === 'Adequate' ? '#2e7d32' :
                                                                            status.status === 'Inadequate' ? '#c62828' : '#666',
                                                                        border: `1px solid ${status.status === 'Adequate' ? '#a5d6a7' :
                                                                            status.status === 'Inadequate' ? '#ef9a9a' : '#ddd'
                                                                            }`
                                                                    }}
                                                                >
                                                                    {status.status || 'Not Assessed'}
                                                                </Box>
                                                                {status.status === 'Inadequate' && status.remarks && (
                                                                    <Tooltip title={status.remarks} arrow>
                                                                        <Typography variant="caption" sx={{ color: '#e62300ff', fontStyle: 'italic' }}>
                                                                            "{status.remarks}"
                                                                        </Typography>
                                                                    </Tooltip>
                                                                )}
                                                                {status.ClauseNo && (
                                                                    <Chip
                                                                        label={`Clause: ${status.ClauseNo}`}
                                                                    />
                                                                )}
                                                            </Box>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Chip
                                                    label={staff.EmployeeType}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        '& .MuiChip-label': {
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {staff.Name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976d2' }}>
                                                    {staff.Designation}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={staff.Qualification}
                                                    size="small"
                                                    color="secondary"
                                                    variant="filled"
                                                    sx={{ fontSize: '0.75rem' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: '#666' }}>
                                                    {staff.Specialization}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={`${staff.RelevantExperience} years`}
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                    icon={<TimeIcon />}
                                                    sx={{ fontSize: '0.75rem' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={staff.RelevantTraining}>
                                                    <Typography variant="body2" sx={{
                                                        maxWidth: '150px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        color: '#666'
                                                    }}>
                                                        {staff.RelevantTraining}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <PhoneIcon fontSize="small" sx={{ color: '#4caf50' }} />
                                                    <Typography variant="body2">
                                                        {staff.ContactNo}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ color: '#666' }}>
                                                    {staff.FaxNo || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmailIcon fontSize="small" sx={{ color: '#2196f3' }} />
                                                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                        {staff.EmailID}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {staff.DisciplineName ? (
                                                    <Chip
                                                        label={staff.DisciplineName}
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.75rem' }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#999' }}>-</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={staff.IsAuthorisedSignatory ? 'Authorized' : 'Not Authorized'}
                                                    size="small"
                                                    color={staff.IsAuthorisedSignatory ? 'success' : 'error'}
                                                    variant="filled"
                                                    icon={staff.IsAuthorisedSignatory ? <CheckIcon /> : <CancelIcon />}
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {staff.ISO15189Training || '-'}
                                            </TableCell>

                                            <TableCell>
                                                {staff.CompetenceAssessmentPerson || '-'}
                                            </TableCell>



                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 1,
                                                        minWidth: '250px'
                                                    }}
                                                >

                                                    {staff.BioDataFile && (
                                                        <Chip
                                                            label="Bio Data"
                                                            size="small"
                                                            color="primary"
                                                            clickable
                                                            onClick={() =>
                                                                s3Utils.handleFileViewWithS3(
                                                                    staff.BioDataFile,
                                                                    {
                                                                        showError: (msg) =>
                                                                            showSnackbar(msg, 'error'),
                                                                        expiresIn: 172800
                                                                    }
                                                                )
                                                            }
                                                            icon={<FileIcon />}
                                                        />
                                                    )}

                                                    {staff.QualificationCertificateFile && (
                                                        <Chip
                                                            label="Qualification"
                                                            size="small"
                                                            color="secondary"
                                                            clickable
                                                            onClick={() =>
                                                                s3Utils.handleFileViewWithS3(
                                                                    staff.QualificationCertificateFile,
                                                                    {
                                                                        showError: (msg) =>
                                                                            showSnackbar(msg, 'error'),
                                                                        expiresIn: 172800
                                                                    }
                                                                )
                                                            }
                                                            icon={<FileIcon />}
                                                        />
                                                    )}

                                                    {staff.ExperienceCertificateFile && (
                                                        <Chip
                                                            label="Experience"
                                                            size="small"
                                                            color="success"
                                                            clickable
                                                            onClick={() =>
                                                                s3Utils.handleFileViewWithS3(
                                                                    staff.ExperienceCertificateFile,
                                                                    {
                                                                        showError: (msg) =>
                                                                            showSnackbar(msg, 'error'),
                                                                        expiresIn: 172800
                                                                    }
                                                                )
                                                            }
                                                            icon={<FileIcon />}
                                                        />
                                                    )}

                                                    {staff.TrainingCertificateFile && (
                                                        <Chip
                                                            label="Training"
                                                            size="small"
                                                            color="warning"
                                                            clickable
                                                            onClick={() =>
                                                                s3Utils.handleFileViewWithS3(
                                                                    staff.TrainingCertificateFile,
                                                                    {
                                                                        showError: (msg) =>
                                                                            showSnackbar(msg, 'error'),
                                                                        expiresIn: 172800
                                                                    }
                                                                )
                                                            }
                                                            icon={<FileIcon />}
                                                        />
                                                    )}

                                                    {staff.AppointmentLetterFile && (
                                                        <Chip
                                                            label="Appointment"
                                                            size="small"
                                                            color="info"
                                                            clickable
                                                            onClick={() =>
                                                                s3Utils.handleFileViewWithS3(
                                                                    staff.AppointmentLetterFile,
                                                                    {
                                                                        showError: (msg) =>
                                                                            showSnackbar(msg, 'error'),
                                                                        expiresIn: 172800
                                                                    }
                                                                )
                                                            }
                                                            icon={<FileIcon />}
                                                        />
                                                    )}

                                                    {staff.CompetenceAssessmentFile && (
                                                        <Chip
                                                            label="Assessment"
                                                            size="small"
                                                            color="error"
                                                            clickable
                                                            onClick={() =>
                                                                s3Utils.handleFileViewWithS3(
                                                                    staff.CompetenceAssessmentFile,
                                                                    {
                                                                        showError: (msg) =>
                                                                            showSnackbar(msg, 'error'),
                                                                        expiresIn: 172800
                                                                    }
                                                                )
                                                            }
                                                            icon={<FileIcon />}
                                                        />
                                                    )}

                                                    {!staff.BioDataFile &&
                                                        !staff.QualificationCertificateFile &&
                                                        !staff.ExperienceCertificateFile &&
                                                        !staff.TrainingCertificateFile &&
                                                        !staff.AppointmentLetterFile &&
                                                        !staff.CompetenceAssessmentFile && (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ color: '#999' }}
                                                            >
                                                                No Documents
                                                            </Typography>
                                                        )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {staff.SignatorySign ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Chip
                                                            label={extractOriginalFileName(staff.SignatorySign)}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                            icon={<FileIcon />}
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                maxWidth: '140px',
                                                                '& .MuiChip-label': {
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }
                                                            }}
                                                        />
                                                        <Tooltip title="View File">
                                                            <IconButton
                                                                size="small"
                                                                sx={{
                                                                    color: '#4caf50',
                                                                    '&:hover': {
                                                                        backgroundColor: '#e8f5e8',
                                                                        color: '#2e7d32'
                                                                    }
                                                                }}
                                                                onClick={() => s3Utils.handleFileViewWithS3(
                                                                    staff.SignatorySign,
                                                                    {
                                                                        showError: (msg) => showSnackbar(msg, 'error'),
                                                                        expiresIn: 172800 // 48 hours
                                                                    }
                                                                )}
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    <Chip
                                                        label="No file uploaded"
                                                        size="small"
                                                        color="default"
                                                        variant="outlined"
                                                        sx={{
                                                            fontSize: '0.75rem',
                                                            color: '#999'
                                                        }}
                                                    />
                                                )}
                                                {staff.photo ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Chip
                                                            label={extractOriginalFileName(staff.photo)}
                                                            size="small"
                                                            variant="outlined"
                                                            icon={<FileIcon />}
                                                            sx={{
                                                                color: '#1976d2',
                                                                fontSize: '0.75rem',
                                                                maxWidth: '140px',
                                                                '& .MuiChip-label': {
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }
                                                            }}
                                                        />
                                                        <Tooltip title="View Photo">
                                                            <IconButton
                                                                size="small"
                                                                sx={{
                                                                    color: '#1976d2',
                                                                    '&:hover': {
                                                                        backgroundColor: '#e8f5e8',
                                                                        color: '#1976d2'
                                                                    }
                                                                }}
                                                                onClick={() => s3Utils.handleFileViewWithS3(
                                                                    staff.photo,
                                                                    {
                                                                        showError: (msg) => showSnackbar(msg, 'error'),
                                                                        expiresIn: 172800 // 48 hours
                                                                    }
                                                                )}
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>
                                                {staff.typeofEngagement ? (
                                                    <Chip
                                                        label={staff.typeofEngagement}
                                                        size="small"
                                                        color="info"
                                                        variant="filled"
                                                        sx={{ fontSize: '0.75rem' }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#999' }}>-</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {staff.Timming ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <TimeIcon fontSize="small" sx={{ color: '#ff9800' }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {staff.Timming}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: '#999' }}>-</Typography>
                                                )}
                                            </TableCell>

                                            <TableCell sx={{ width: '30px' }}>
                                                {staff.CEOName ? (
                                                    <Typography variant='caption' color='primary' fontSize='small'>
                                                        I, <strong>{staff.CEOName || '-'}</strong> hereby declare that the above person meets the qualification and experience requirements for the scope as per ISO 15189:2022 and NABL criteria

                                                        <strong> {staff.DeclarationRemark || '-'}</strong> and has been assessed by me to report, review and authorization of results in Histopathology discipline for scope.

                                                        I also understand and accept that in case of the above information found false or ambiguous, NABL has the right to take any adverse actions against the lab as deemed appropriate as per NABL 216.
                                                    </Typography>
                                                ) : (
                                                    '-'
                                                )}

                                            </TableCell>

                                            {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || isSectionUnfrozen('Lab Staff')) && (
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Tooltip title="Edit Staff" arrow>
                                                            <IconButton
                                                                onClick={() => handleEditStaff(staff)}
                                                                size="small"
                                                                sx={{
                                                                    color: '#1976d2',
                                                                    backgroundColor: '#e3f2fd',
                                                                    '&:hover': {
                                                                        backgroundColor: '#bbdefb',
                                                                        color: '#0d47a1'
                                                                    }
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Staff" arrow>
                                                            <IconButton
                                                                onClick={() => handleDeleteStaff(staff.Id)}
                                                                size="small"
                                                                sx={{
                                                                    color: '#d32f2f',
                                                                    backgroundColor: '#ffebee',
                                                                    '&:hover': {
                                                                        backgroundColor: '#ffcdd2',
                                                                        color: '#b71c1c'
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Paper>
        </Box>
    ), [error, loading, staffList, fetchStaffList, selectedItems, handleSelectItem, handleSelectAll, handleMarkAdequate, getStaffStatus, adequacyChecks, applicationStage]);

    return (
        <>
            <Box p={3}>
                <Box sx={{
                    py: 2,
                    px: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 1,
                    mb: 3
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            🏢 Lab Staff
                        </Typography>

                        {/* Unfrozen section indicator */}
                        {isSectionUnfrozen('Lab Staff') && (
                            <Chip
                                label="🔓 UNFROZEN"
                                size="small"
                                sx={{
                                    bgcolor: '#ff9800',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    animation: 'pulse 2s infinite'
                                }}
                            />
                        )}
                    </Box>
                </Box>

                {/* Unfrozen Sections Notification */}
                {isSectionUnfrozen('Lab Staff') && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            🔓 Sections Unfrozen for Editing
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            The Lab Staff section has been temporarily unfrozen for editing. You can now add, modify, and delete staff records.
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Chip
                                label="Lab Staff"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontWeight: 'bold' }}
                            />
                        </Box>
                    </Alert>
                )}

                {/* Tab Navigation */}
                <Paper sx={{ mb: 3 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider'
                        }}
                    >
                        {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || applicationStage === '18' || applicationStage === '18b' || isSectionUnfrozen('Lab Staff')) && (
                            <Tab
                                label={editingStaff ? "Edit Staff" : "Add Staff"}
                                sx={{ fontWeight: 600 }}
                            />
                        )}
                        <Tab
                            label="View Staff"
                            sx={{ fontWeight: 600 }}
                        />
                    </Tabs>
                </Paper>

                {/* Tab Content */}
                {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || applicationStage === '18' || applicationStage === '18b' || isSectionUnfrozen('Lab Staff')) && tabValue === 0 && renderAddStaffTab()}

                {tabValue === ((applicationStage === null || applicationStage === "" || applicationStage === '18a' || applicationStage === '18' || applicationStage === '18b' || isSectionUnfrozen('Lab Staff')) ? 1 : 0) && renderViewStaffTab()}
            </Box>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Adequacy Check Dialog */}
            <Dialog
                open={adequacyDialogOpen}
                onClose={closeAdequacyDialog}
                maxWidth="sm"
                fullWidth
                aria-labelledby="adequacy-dialog-title"
            >
                <DialogTitle id="adequacy-dialog-title" sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600
                }}>
                    ✅ Mark Staff Adequacy Status
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                            Selected Staff Members: <strong>{selectedItems.length}</strong>
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel id="adequacy-status-label">
                                    Adequacy Status
                                </InputLabel>
                                <Select
                                    labelId="adequacy-status-label"
                                    value={adequacyStatus}
                                    label="Adequacy Status"
                                    onChange={(e) => setAdequacyStatus(e.target.value)}
                                >
                                    <MenuItem value="Adequate">Adequate</MenuItem>
                                    <MenuItem value="Inadequate">Inadequate</MenuItem>
                                    {userTypeId === '4' && (
                                        <MenuItem value="To be Verify">To be Verify</MenuItem>
                                    )}

                                </Select>
                            </FormControl>
                        </Grid>
                        {(adequacyStatus === 'Inadequate' && userTypeId === '6') && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    fullWidth
                                    freeSolo
                                    options={clauseNo}
                                    value={selectedClause}
                                    getOptionLabel={(option) => {
                                        if (typeof option === 'string') return option;
                                        return option?.DisplayName || option?.clause_no || option?.text || '';
                                    }}
                                    onChange={(event, newValue) => {
                                        setSelectedClause(newValue);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={`Select Clause Number${clauseNo.length > 0 ? ` (${clauseNo.length} available)` : ''}`}
                                            placeholder={clauseNo.length > 0 ?
                                                "Select clause number to add..." :
                                                "No clause numbers available"
                                            }
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'white',
                                                    borderRadius: 2,
                                                    '&:hover': {
                                                        boxShadow: '0 2px 8px rgba(33,150,243,0.15)',
                                                    },
                                                    '&.Mui-focused': {
                                                        boxShadow: '0 4px 12px rgba(33,150,243,0.25)',
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props} sx={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            padding: '8px 16px !important',
                                            fontSize: '0.875rem',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {typeof option === 'string' ? option : (option.DisplayName || option.clause_no || option.text || '')}
                                                </Typography>
                                                <Chip
                                                    label="Clause"
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.7rem', height: '20px' }}
                                                />
                                            </Box>
                                        </Box>
                                    )}
                                    PopperComponent={(props) => (
                                        <div {...props} style={{ ...props.style, zIndex: 1300 }} />
                                    )}
                                />
                                {selectedClause && (
                                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="caption" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                                            Selected Clause:
                                        </Typography>
                                        <Chip
                                            label={typeof selectedClause === 'string' ? selectedClause : (selectedClause.DisplayName || selectedClause.clause_no || selectedClause.text || '')}
                                            size="small"
                                            color="info"
                                            onDelete={() => setSelectedClause(null)}
                                            sx={{ fontSize: '0.75rem' }}
                                        />
                                    </Box>
                                )}
                            </Grid>
                        )}

                        {adequacyStatus === 'Inadequate' && (
                            <>
                                <Grid item xs={12}>
                                    {/* Simple textarea for remarks */}
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Remarks (Required)"
                                        placeholder="Type your detailed remarks here. Use the dropdown below to select predefined remarks."
                                        value={adequacyRemarks || ''}
                                        onChange={(e) => handleAdequacyRemarksChange(e.target.value, false)}
                                        required
                                        variant="outlined"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: 'white',
                                                borderRadius: 2,
                                                '&:hover': {
                                                    boxShadow: '0 2px 8px rgba(220,53,69,0.15)',
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 4px 12px rgba(220,53,69,0.25)',
                                                }
                                            }
                                        }}
                                    />

                                    {/* Predefined Remarks Dropdown */}
                                    {scrutinyRemarks.length > 0 && (
                                        <Box sx={{ mt: 2, position: 'relative' }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setIsCheckboxDropdownOpen(!isCheckboxDropdownOpen)}
                                                sx={{
                                                    mb: 1,
                                                    borderColor: '#007bff',
                                                    color: '#007bff',
                                                    '&:hover': { borderColor: '#0056b3', backgroundColor: '#f8f9ff' }
                                                }}
                                            >
                                                {isCheckboxDropdownOpen ? 'Hide' : 'Show'} Predefined Remarks ({scrutinyRemarks.length} available)
                                            </Button>

                                            {selectedRemarksOptions.length > 0 && (
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    onClick={handleClearAllCheckboxes}
                                                    sx={{ ml: 1, color: '#dc3545' }}
                                                >
                                                    Clear All ({selectedRemarksOptions.length})
                                                </Button>
                                            )}

                                            {isCheckboxDropdownOpen && (
                                                <Paper
                                                    elevation={3}
                                                    sx={{
                                                        mt: 1,
                                                        maxHeight: 300,
                                                        overflow: 'auto',
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 2
                                                    }}
                                                >
                                                    <MenuList dense>
                                                        {scrutinyRemarks.map((option, index) => {
                                                            const remarkText = typeof option === 'string' ? option : option.inadequate_remarks;
                                                            const isSelected = selectedRemarksOptions.some(
                                                                item => (typeof item === 'string' ? item : item.inadequate_remarks) === remarkText
                                                            );

                                                            return (
                                                                <MenuItem
                                                                    key={index}
                                                                    onClick={() => handleCheckboxChange(option, !isSelected)}
                                                                    sx={{
                                                                        py: 1,
                                                                        px: 2,
                                                                        display: 'flex',
                                                                        alignItems: 'flex-start',
                                                                        minHeight: 'auto'
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        size="small"
                                                                        sx={{ p: 0, mr: 2 }}
                                                                    />
                                                                    <Box sx={{ flexGrow: 1 }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: '0.875rem',
                                                                                lineHeight: 1.4,
                                                                                wordBreak: 'break-word',
                                                                                whiteSpace: 'pre-wrap'
                                                                            }}
                                                                        >
                                                                            {remarkText}
                                                                        </Typography>
                                                                        {typeof option !== 'string' && option.type && (
                                                                            <Chip
                                                                                label={option.type}
                                                                                size="small"
                                                                                color={option.type === 'Assessment' ? 'primary' : 'secondary'}
                                                                                variant="outlined"
                                                                                sx={{
                                                                                    fontSize: '0.7rem',
                                                                                    height: '18px',
                                                                                    mt: 0.5
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                </MenuItem>
                                                            );
                                                        })}
                                                    </MenuList>
                                                </Paper>
                                            )}
                                        </Box>
                                    )}

                                    <Typography variant="caption" sx={{
                                        color: '#6c757d',
                                        display: 'block',
                                        mt: 1,
                                        fontStyle: 'italic'
                                    }}>
                                        {scrutinyRemarksLoading
                                            ? "Loading existing remarks..."
                                            : scrutinyRemarks.length > 0
                                                ? `${scrutinyRemarks.length} predefined remark(s) available for "Lab Staff". You can select multiple options from the dropdown above.`
                                                : "No predefined remarks found for this section. Type your detailed remarks in the text area above."
                                        }
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        {adequacyStatus === 'Adequate' && (
                            <Grid item xs={12}>
                                <Alert severity="success">
                                    <Typography variant="body2">
                                        ✅ Selected staff members will be marked as <strong>Adequate</strong>.
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button
                        onClick={closeAdequacyDialog}
                        color="primary"
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAdequacySubmit}
                        color="success"
                        variant="contained"
                        disabled={
                            loading ||
                            !adequacyStatus ||
                            (adequacyStatus === 'Inadequate' && (!adequacyRemarks?.trim()))
                        }
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                        sx={{
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {loading ? 'Updating...' : 'Submit Adequacy'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LabStaffSection;
